import { useEffect, useState } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { TrendingUp, Loader2 } from "lucide-react";
import { api, formatUSD } from "@/lib/api";

const NAVY = "#0B1F3A";
const RED = "#E63946";

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const rev = payload.find((p) => p.dataKey === "revenue")?.value ?? 0;
  const ord = payload.find((p) => p.dataKey === "orders")?.value ?? 0;
  return (
    <div className="rounded-xl bg-white border border-brand-line shadow-card px-3.5 py-2.5 text-sm">
      <p className="font-bold text-brand-navy mb-1">Week of {label}</p>
      <p className="text-brand-red font-semibold">{formatUSD(rev)} revenue</p>
      <p className="text-brand-navy font-semibold">{ord} paid order{ord === 1 ? "" : "s"}</p>
    </div>
  );
}

export function SalesSnapshot() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/admin/analytics")
      .then(({ data }) => { if (alive) setData(data.weekly || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const hasData = data.some((w) => w.revenue > 0 || w.orders > 0);

  return (
    <div className="rounded-2xl bg-white border border-brand-line p-5 shadow-card" data-testid="sales-snapshot">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-brand-navy">
            <TrendingUp className="w-5 h-5 text-white" strokeWidth={2.2} />
          </span>
          <div>
            <h2 className="font-display font-bold text-lg text-brand-navy leading-tight">Sales snapshot</h2>
            <p className="text-sm text-brand-ink2">Weekly revenue &amp; new orders · last 8 weeks</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="h-[260px] grid place-items-center">
          <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
        </div>
      ) : !hasData ? (
        <div className="h-[260px] grid place-items-center text-brand-ink2 text-sm" data-testid="sales-snapshot-empty">
          No paid orders yet — your revenue trend will appear here.
        </div>
      ) : (
        <div style={{ width: "100%", height: 280 }} data-testid="sales-snapshot-chart">
          <ResponsiveContainer>
            <ComposedChart data={data} margin={{ top: 6, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#455A64", fontSize: 12, fontWeight: 600 }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
              <YAxis yAxisId="left" tick={{ fill: "#455A64", fontSize: 12 }} tickLine={false} axisLine={false}
                tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} />
              <YAxis yAxisId="right" orientation="right" allowDecimals={false} tick={{ fill: "#455A64", fontSize: 12 }} tickLine={false} axisLine={false} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(11,31,58,0.04)" }} />
              <Legend wrapperStyle={{ fontSize: 13, fontWeight: 600, paddingTop: 8 }} />
              <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill={RED} radius={[6, 6, 0, 0]} maxBarSize={38} />
              <Line yAxisId="right" type="monotone" dataKey="orders" name="Orders" stroke={NAVY} strokeWidth={3} dot={{ r: 4, fill: NAVY }} activeDot={{ r: 6 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
